import React, { useState, useEffect } from 'react';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useCreateArticleTag, useUpdateArticleTag } from '@/hooks/useArticles';
import { useArticleTagDialog } from '@/contexts/ArticleTagContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { UnifiedLLMService } from '@/services/llm/UnifiedLLMService';
import { ApiCallsAuditService } from '@/services/apiCallsAuditService';

const COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'
];

export const GlobalArticleTagDialog: React.FC = () => {
  const { currentBrandId: selectedBrandId } = useBrandManager();
  const createTag = useCreateArticleTag();
  const updateTag = useUpdateArticleTag();
  const { isOpen, editingTag, closeTagDialog } = useArticleTagDialog();

  // Form state persists across tab changes
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(COLORS[0]);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  // Sync form when editing tag changes
  useEffect(() => {
    if (editingTag) {
      setName(editingTag.name);
      setSlug(editingTag.slug);
      setDescription(editingTag.description || '');
      setColor(editingTag.color);
    } else {
      setName('');
      setSlug('');
      setDescription('');
      setColor(COLORS[0]);
    }
    setSuggestions([]);
  }, [editingTag, isOpen]);

  const resetForm = () => {
    setName('');
    setSlug('');
    setDescription('');
    setColor(COLORS[0]);
    setSuggestions([]);
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      resetForm();
      closeTagDialog();
    }
  };

  const handleSave = async () => {
    if (!selectedBrandId || !name.trim()) {
      toast.error('Name is required');
      return;
    }

    const tagSlug = slug || name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

    try {
      if (editingTag) {
        await updateTag.mutateAsync({
          id: editingTag.id,
          name,
          slug: tagSlug,
          description: description || null,
          color,
        });
        toast.success('Tag updated');
      } else {
        await createTag.mutateAsync({
          brand_id: selectedBrandId,
          name,
          slug: tagSlug,
          description: description || null,
          color,
        });
        toast.success('Tag created');
      }
      resetForm();
      closeTagDialog();
    } catch (error) {
      console.error('Error saving tag:', error);
    }
  };

  const handleGenerateDescription = async () => {
    if (!name.trim()) {
      toast.error('Please enter a tag name first');
      return;
    }

    if (!selectedBrandId) {
      toast.error('No brand selected');
      return;
    }

    setIsGeneratingDescription(true);
    setSuggestions([]);
    ApiCallsAuditService.startTracking();

    try {
      const response = await UnifiedLLMService.callLLM({
        brandId: selectedBrandId,
        source: 'article_tag_description',
        messages: [
          {
            role: 'system',
            content: 'You are a helpful SEO and content expert. Generate exactly 3 unique, SEO-friendly meta descriptions for article tag archive pages. Each description should be 1-2 sentences, around 120-160 characters, and be optimized for search engines. Return ONLY a JSON array with 3 strings, no additional text.'
          },
          {
            role: 'user',
            content: `Generate 3 different SEO-friendly meta descriptions for an article tag named "${name}". These will be used on the tag archive page. Return as a JSON array of 3 strings.`
          }
        ],
        max_tokens: 500
      });

      // Log the API call
      await ApiCallsAuditService.logApiCall({
        source_menu_item: 'article_tag_description',
        platform: (response.provider as any) || 'openai',
        brand_id: selectedBrandId,
        model_used: response.model,
        status_code: 200,
        metadata: { tag_name: name }
      });

      // Parse the response
      const content = response.content.trim();
      let parsed: string[] = [];
      
      try {
        // Try to extract JSON array from response
        const jsonMatch = content.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          parsed = JSON.parse(jsonMatch[0]);
        } else {
          // Fallback: split by newlines if no JSON array
          parsed = content.split('\n').filter(line => line.trim()).slice(0, 3);
        }
      } catch {
        // Fallback to single description
        parsed = [content];
      }

      if (parsed.length > 0) {
        setSuggestions(parsed.slice(0, 3));
        toast.success('Descriptions generated - click to select');
      }
    } catch (error) {
      console.error('Error generating description:', error);
      toast.error('Failed to generate descriptions');
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const handleSelectSuggestion = (suggestion: string) => {
    setDescription(suggestion);
    setSuggestions([]);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitleWithBug
            popupName={editingTag ? 'Edit Article Tag' : 'New Article Tag'}
            popupContext="/articles/tags"
            isOpen={isOpen}
          >
            {editingTag ? 'Edit Tag' : 'New Tag'}
          </DialogTitleWithBug>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              placeholder="Tag name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Slug</Label>
            <Input
              placeholder="tag-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            />
            <p className="text-xs text-muted-foreground">Leave empty to auto-generate from name</p>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Description (SEO)</Label>
              <Button 
                type="button" 
                variant="ghost" 
                size="sm"
                onClick={handleGenerateDescription}
                disabled={isGeneratingDescription || !name.trim()}
                className="h-7 px-2 text-xs"
              >
                {isGeneratingDescription ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <Sparkles className="h-3 w-3 mr-1" />
                )}
                AI Suggest
              </Button>
            </div>
            <Textarea
              placeholder="Brief description for SEO meta tags..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
            <p className="text-xs text-muted-foreground">Used for tag archive page meta description</p>
            {suggestions.length > 0 && (
              <div className="space-y-2 mt-2">
                <p className="text-xs text-muted-foreground">Click to select:</p>
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSelectSuggestion(suggestion)}
                    className="w-full text-left text-sm p-2 rounded-md border bg-muted/50 hover:bg-muted transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex gap-2 flex-wrap">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    color === c ? 'border-foreground scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleDialogClose(false)}>Cancel</Button>
          <Button onClick={handleSave}>
            {editingTag ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
